/* eslint-disable @typescript-eslint/camelcase */
import i18n from 'i18n';
import { messagesRequest } from 'modules/messages/actions';
import { modalClose } from 'modules/modal/actions';
import { push } from 'react-router-redux';
import { all, put, takeLatest } from 'redux-saga/effects';
import { isRequestOK } from 'utils/helpers';
import { getPublishHistoryRequest } from 'modules/environments/actions';
import {
  deletePartnerFulfilled,
  deletePartnerRejected,
  deletePartnerRequest,
  editPartnerFulfilled,
  editPartnerRejected,
  editPartnerRequest,
  getAllPartnersFulfilled,
  getAllPartnersRejected,
  getAllPartnersRequest,
  getPartnerFulfilled,
  getPartnerRejected,
  getPartnerRequest,
  LDAPTestFulfilled,
  LDAPTestRejected,
  LDAPTestRequest,
  newPartnerFulfilled,
  newPartnerRejected,
  newPartnerRequest,
  toggleBlockPartnerFulfilled,
  toggleBlockPartnerRejected,
  toggleBlockPartnerRequest,
  getCustomStateRequest,
  getCustomStateFulfilled,
  getCustomStateRejected,
  saveCustomRequest,
  saveCustomFulfilled,
  saveCustomRejected,
  getAllPartnersOperatorFulfilled,
  getAllPartnersOperatorRejected,
  getAllPartnersOperatorRequest,
  publishCustomFulfilled,
  publishCustomRejected,
  publishCustomRequest,
  editCustomFulfilled,
  editCustomRejected,
  editCustomRequest,
  removeImageCustomFulfilled,
  removeImageCustomRejected,
  removeImageCustomRequest,
  deleteCustomFulfilled,
  deleteCustomRejected,
  deleteCustomRequest,
  getAllPartnerServersFulfilled,
  getAllPartnerServersRejected,
  getAllPartnerServersRequest,
  startPartnerServersFulfilled,
  startPartnerServersRejected,
  startPartnerServersRequest,
  stopPartnerServersFulfilled,
  stopPartnerServersRejected,
  stopPartnerServersRequest,
  publishPartnerServersFulfilled,
  publishPartnerServersRejected,
  publishPartnerServersRequest,
  clonePartnerFulfilled,
  clonePartnerRejected,
  clonePartnerRequest,
  changeAdPasswordFulfilled,
  changeAdPasswordRejected,
  changeAdPasswordRequest,
  getLDAPTypeReportFulfilled,
  getLDAPTypeReportRejected,
  getLDAPTypeReportRequest,
  getNewFrontReportFulfilled,
  getNewFrontReportRejected,
  getNewFrontReportRequest,
  getPartnerProviderTypeReportFulfilled,
  getPartnerProviderTypeReportRejected,
  getPartnerProviderTypeReportRequest,
  getSSOReportFulfilled,
  getSSOReportRejected,
  getSSOReportRequest,
  exportPartnerSummaryFulfilled,
  exportPartnerSummaryRejected,
  exportPartnerSummaryRequest,
  publishImagePartnerFulfilled,
  publishImagePartnerRejected,
  publishImagePartnerRequest,
  blockServerPartnerActionsFulfilled,
  blockServerPartnerActionsRejected,
  blockServerPartnerActionsRequest,
  unblockServerPartnerActionsFulfilled,
  unblockServerPartnerActionsRejected,
  unblockServerPartnerActionsRequest,
  getInstancesByUsageRequest,
  getInstancesByUsageFulfilled,
  getInstancesByUsageRejected,
  getLeakedCredentialsUsersFulfilled,
  getLeakedCredentialsUsersRejected,
  getLeakedCredentialsUsersRequest,
  getLeakedCredentialsUsersClientsRequest,
  getLeakedCredentialsUsersClientsFulfilled,
  getLeakedCredentialsUsersClientsRejected,
  resetPasswordLeakedCredentialsRequest,
  resetPasswordLeakedCredentialsFulfilled,
  resetPasswordLeakedCredentialsRejected,
} from './actions';
import endpoints, { api } from './endpoints';

function* newPartnerRequestSaga({ payload }) {
  try {
    const { status, data } = yield api.post({
      url: endpoints.newPartner(),
      data: payload,
    });

    if (!isRequestOK(status)) return yield put(newPartnerRejected());

    const redirectTo = `/partners/${data?.partner_uuid}/configuration`;
    return yield all([
      put(newPartnerFulfilled()),
      put(
        messagesRequest({
          type: 'success',
          message: i18n.t('PARTNER_PAGE.SUCCESS_CREATE_ACCOUNT'),
        })
      ),
      put(push(redirectTo)),
    ]);
  } catch (error) {
    return yield put(newPartnerRejected());
  }
}

function* getCustomStateRequestSaga({ payload }) {
  try {
    const { data } = yield api.get({
      url: endpoints.getTheme(payload),
    });

    return yield all([put(getCustomStateFulfilled(data))]);
  } catch (error) {
    return yield put(getCustomStateRejected());
  }
}

function* saveCustomRequestSaga({ payload }) {
  const { id, values } = payload;

  const formData = new FormData();

  Object.keys(values).forEach(key => {
    if (key === 'primary' || key === 'secondary') {
      formData.append(key, JSON.stringify(values[key]));
    } else {
      formData.append(key, values[key]);
    }
  });

  try {
    yield api.post({
      url: endpoints.saveTheme(id),
      header: {
        'Content-Type': 'multipart/form-data',
      },
      data: formData,
    });

    yield put(saveCustomFulfilled());

    return yield put(publishCustomRequest({ id }));
  } catch (error) {
    return yield all([
      put(
        messagesRequest({
          type: 'error',
          message: i18n.t('UNEXPECTED_ERROR'),
        })
      ),
      put(saveCustomRejected()),
    ]);
  }
}

function* removeImageCustomRequestSaga({ payload }) {
  const { id, values } = payload;

  try {
    yield api.post({
      url: endpoints.removeImage(id),
      data: { image_types: values },
    });

    return yield all([
      put(removeImageCustomFulfilled()),
      put(getCustomStateRequest(id)),
      put(modalClose()),
    ]);
  } catch (error) {
    return yield put(removeImageCustomRejected());
  }
}

function* editCustomRequestSaga({ payload }) {
  const { id, values } = payload;

  const formData = new FormData();

  Object.keys(values).forEach(key => {
    if (key === 'primary' || key === 'secondary') {
      formData.append(key, JSON.stringify(values[key]));
    } else {
      formData.append(key, values[key]);
    }
  });

  try {
    yield api.put({
      url: endpoints.editTheme(id),
      header: {
        'Content-Type': 'multipart/form-data',
      },
      data: formData,
    });

    yield put(editCustomFulfilled());

    return yield put(publishCustomRequest({ id }));
  } catch (error) {
    return yield all([
      put(
        messagesRequest({
          type: 'error',
          message: i18n.t('UNEXPECTED_ERROR'),
        })
      ),
      put(editCustomRejected()),
    ]);
  }
}

function* publishCustomRequestSaga({ payload }) {
  const { id } = payload;

  const formData = new FormData();

  try {
    yield api.post({
      url: endpoints.publishTheme(id),
      header: {
        'Content-Type': 'multipart/form-data',
      },
      data: formData,
    });

    return yield all([
      put(publishCustomFulfilled()),
      put(
        messagesRequest({
          type: 'success',
          message: i18n.t('PARTNER_PAGE.SUCCESS_PUBLISH_KEY'),
        })
      ),
      put(getCustomStateRequest(id)),
    ]);
  } catch (error) {
    return yield put(publishCustomRejected());
  }
}

function* deleteCustomRequestSaga({ payload }) {
  const { restricted_action_code, id } = payload;
  try {
    yield api.post({
      url: endpoints.deleteTheme(id),
      data: { restricted_action_code },
    });

    yield put(deleteCustomFulfilled());

    if (restricted_action_code) {
      yield all([
        put(
          messagesRequest({
            type: 'success',
            message: i18n.t('PARTNER_PAGE.DELETE_CUSTOM_SUCCESS_KEY'),
          })
        ),
        put(push('/all-partners')),
      ]);
    }
  } catch (error) {
    yield put(deleteCustomRejected());
  }
}

function* newLDAPTestRequestSaga({ payload }) {
  const { values, partnerUuid, handleOpenTestResult } = payload;
  try {
    const { data } = yield api.post({
      url: endpoints.LDAPTest(partnerUuid),
      data: values,
    });
    return yield all([
      put(LDAPTestFulfilled(data)),
      put(handleOpenTestResult(data)),
    ]);
  } catch (error) {
    return yield all([put(LDAPTestRejected())]);
  }
}

function* editPartnerRequestSaga({ payload }) {
  try {
    const { status } = yield api.patch({
      url: endpoints.editPartner(payload.partner_uuid),
      data: payload,
    });

    if (!isRequestOK(status)) return yield put(editPartnerRejected());

    return yield all([
      put(editPartnerFulfilled()),
      put(
        messagesRequest({
          type: 'success',
          message: i18n.t('SUCCESS_PARTNER_EDIT_KEY'),
        })
      ),
      put(getPartnerRequest(payload.partner_uuid)),
    ]);
  } catch (error) {
    return yield put(editPartnerRejected());
  }
}

function* getPartnerRequestSaga({ payload }) {
  try {
    const { data } = yield api.get({
      url: endpoints.getPartner(payload),
    });
    return yield put(getPartnerFulfilled(data));
  } catch (error) {
    return yield all([put(getPartnerRejected()), put(push('/partners'))]);
  }
}

function* getAllPartnersRequestSaga({ payload }) {
  const { company_uuid, query } = payload;

  try {
    const { data } = yield api.get({
      url: endpoints.getAllPartners({
        ...query,
        company_uuid: company_uuid || '',
      }),
    });

    return yield put(getAllPartnersFulfilled(data));
  } catch (error) {
    return yield put(getAllPartnersRejected());
  }
}

function* getAllPartnersOperatorRequestSaga({ payload }) {
  try {
    const { data } = yield api.get({
      url: endpoints.getAllPartnersOperator(payload),
    });

    return yield put(getAllPartnersOperatorFulfilled(data));
  } catch (error) {
    return yield put(getAllPartnersOperatorRejected());
  }
}

function* deletePartnerRequestSaga({ payload }) {
  const { query, partnerUuid, company_uuid } = payload;
  try {
    const { data } = yield api.delete({
      url: endpoints.deletePartner(partnerUuid),
    });

    yield put(
      messagesRequest({
        type: 'success',
        message: i18n.t('PARTNER_PAGE.PARTNER_DELETED_KEY'),
      })
    );

    yield put(getAllPartnersRequest({ ...query, company_uuid }));

    return yield put(deletePartnerFulfilled(data));
  } catch (error) {
    return yield put(deletePartnerRejected());
  }
}

function* toggleBlockPartnerRequestSaga({ payload }) {
  const { partner_uuid, partner_status, query, company_uuid } = payload;

  try {
    yield api.patch({
      url: endpoints.editPartner(partner_uuid),
      data: {
        partner_status,
      },
    });

    if (query) yield put(getAllPartnersRequest({ ...query, company_uuid }));

    return yield all([
      put(toggleBlockPartnerFulfilled()),
      put(
        messagesRequest({
          type: 'success',
          message: i18n.t(
            partner_status === 'active'
              ? 'PARTNER_UNBLOCKED_SUCCESSFULLY_KEY'
              : 'PARTNER_BLOCKED_SUCCESSFULLY_KEY'
          ),
        })
      ),
    ]);
  } catch (error) {
    return yield put(toggleBlockPartnerRejected());
  }
}

function* getAllPartnerServersRequestSaga({ payload }) {
  const { id, server_type, query } = payload;

  try {
    const { data } = yield api.get({
      url: endpoints.getAllPartnerServers(id, server_type, query),
    });

    return yield put(getAllPartnerServersFulfilled(data));
  } catch (error) {
    return yield put(getAllPartnerServersRejected());
  }
}
function* startPartnerServersRequestSaga({ payload }) {
  const { id, instance_ids, query, server_type } = payload;

  try {
    const { data } = yield api.post({
      url: endpoints.startPartnerServers(id),
      data: { instance_ids },
    });

    return yield all([
      put(startPartnerServersFulfilled()),
      put(
        messagesRequest({
          type: 'success',
          message: i18n.t(`${data.message.code.toUpperCase()}`),
        })
      ),
      put(getAllPartnerServersRequest({ id, server_type, query })),
      put(modalClose()),
    ]);
  } catch (error) {
    return yield put(startPartnerServersRejected());
  }
}
function* stopPartnerServersRequestSaga({ payload }) {
  const { id, instance_ids, query, server_type } = payload;

  try {
    const { data } = yield api.post({
      url: endpoints.stopPartnerServers(id),
      data: { instance_ids },
    });

    return yield all([
      put(stopPartnerServersFulfilled()),
      put(
        messagesRequest({
          type: 'success',
          message: i18n.t(`${data.message.code.toUpperCase()}`),
        })
      ),
      put(getAllPartnerServersRequest({ id, server_type, query })),
      put(modalClose()),
    ]);
  } catch (error) {
    return yield put(stopPartnerServersRejected());
  }
}

function* publishPartnerServersRequestSaga({ payload }) {
  const {
    id,
    values,
    publication_type,
    instance_ids,
    query,
    server_type,
  } = payload;

  const emailsList: string[] = values?.notification_email
    .split(',')
    .filter(Boolean);

  try {
    yield api.post({
      url: endpoints.publishPartnerServers(id),
      data: {
        notification_email: emailsList,
        operator_name: values?.operator_name,
        disconnection_time: values?.disconnection_time,
        user_message: values?.user_message,
        publication_type,
        instance_ids,
      },
    });

    return yield all([
      put(publishPartnerServersFulfilled()),
      put(
        messagesRequest({
          type: 'success',
          message: i18n.t('ENVIRONMENTS_PAGE.NEW_PUBLICATION'),
        })
      ),
      put(getAllPartnerServersRequest({ id, server_type, query })),
      put(modalClose()),
    ]);
  } catch (error) {
    return yield put(publishPartnerServersRejected());
  }
}

function* clonePartnerRequestSaga({ payload }) {
  const { partnerUuid, values, company_uuid, query } = payload;

  try {
    yield api.post({
      url: endpoints.clonePartner(partnerUuid),
      data: values,
    });

    return yield all([
      put(clonePartnerFulfilled()),
      put(
        messagesRequest({
          type: 'success',
          message: i18n.t('PARTNER_CLONE_MESSAGE_KEY'),
        })
      ),
      put(
        company_uuid
          ? getAllPartnersRequest({ query, company_uuid })
          : getAllPartnersOperatorRequest({ ...query })
      ),
    ]);
  } catch (error) {
    return yield put(clonePartnerRejected());
  }
}

function* changeAdPasswordRequestSaga({ payload }) {
  const { partnerUuid, values } = payload;

  try {
    yield api.post({
      url: endpoints.changeAdPassword(partnerUuid),
      data: values,
    });

    yield put(changeAdPasswordFulfilled());

    if (values.restricted_action_code) {
      yield all([
        put(
          messagesRequest({
            type: 'success',
            message: i18n.t('CHANGE_AD_PASSWORD_MESSAGE_KEY'),
          })
        ),
        put(modalClose()),
        put(getPartnerRequest(partnerUuid)),
      ]);
    }
  } catch (error) {
    yield put(changeAdPasswordRejected());
  }
}

function* getLDAPTypeReportRequestSaga({ payload }) {
  const { filters } = payload;
  try {
    const { data } = yield api.get({
      url: endpoints.getLDAPTypeReport(filters),
    });

    return yield put(getLDAPTypeReportFulfilled(data));
  } catch (error) {
    return yield put(getLDAPTypeReportRejected());
  }
}

function* getSSOReportRequestSaga({ payload }) {
  const { filters } = payload;
  try {
    const { data } = yield api.get({
      url: endpoints.getSSOReport(filters),
    });

    return yield put(getSSOReportFulfilled(data));
  } catch (error) {
    return yield put(getSSOReportRejected());
  }
}

function* getNewFrontReportRequestSaga({ payload }) {
  const { filters } = payload;
  try {
    const { data } = yield api.get({
      url: endpoints.getNewFrontReport(filters),
    });

    return yield put(getNewFrontReportFulfilled(data));
  } catch (error) {
    return yield put(getNewFrontReportRejected());
  }
}

function* getPartnerProviderTypeReportRequestSaga({ payload }) {
  const { filters } = payload;
  try {
    const { data } = yield api.get({
      url: endpoints.getPartnerProviderTypeReport(filters),
    });

    return yield put(getPartnerProviderTypeReportFulfilled(data));
  } catch (error) {
    return yield put(getPartnerProviderTypeReportRejected());
  }
}

function* exportPartnerSummaryRequestSaga({ payload }) {
  const { query } = payload;
  try {
    const { data } = yield api.get({
      url: endpoints.exportPartnerSummary(query),
    });

    const elm = document.createElement('a');
    elm.href = `data:text/csv;charset=utf-8,${encodeURIComponent(data)}`;
    elm.target = '_blank';
    elm.download = `${i18n.t('PARTNERS_KEY')}-${new Date().toLocaleString(
      'pt-BR',
      {
        hour12: false,
      }
    )}-summary.csv`;

    elm.click();

    return yield all([
      put(
        messagesRequest({
          type: 'success',
          message: i18n.t('EXPORT_CSV_SUCCESS_KEY'),
        })
      ),
      put(exportPartnerSummaryFulfilled(data)),
    ]);
  } catch (error) {
    return yield put(exportPartnerSummaryRejected());
  }
}

function* publishImagePartnerRequestSaga({ payload }) {
  const { partnerUuid, values, environmentUuid, inlineAction } = payload;

  const emailsList: string[] = values?.notify_list.split(',').filter(Boolean);

  if (values?.replace_instances?.block_users === false) {
    delete values.replace_instances.block_users_message;
    delete values.replace_instances.block_users_seconds;
  }

  try {
    yield api.post({
      url: endpoints.publishImagePartner(partnerUuid),
      data: { ...values, notify_list: emailsList },
    });

    const actions = [
      put(publishImagePartnerFulfilled()),
      put(
        messagesRequest({
          type: 'success',
          message: inlineAction
            ? i18n.t('PUBLISHING_STARTED_SUCCESSFULLY_KEY')
            : i18n.t('PUBLISH_IN_BULK_SUCCESS_KEY'),
        })
      ),
      put(getPublishHistoryRequest({ environmentUuid })),
    ];

    return yield all(actions);
  } catch (error) {
    return yield put(publishImagePartnerRejected());
  }
}

function* blockServerPartnerActionsRequestSaga({ payload }) {
  const { partnerUuid, instanceId, values, query } = payload;
  try {
    yield api.post({
      url: endpoints.blockServerPartnerActions(partnerUuid),
      data: { ...values, instance_ids: instanceId },
    });

    return yield all([
      put(blockServerPartnerActionsFulfilled()),
      put(
        messagesRequest({
          type: 'success',
          message: i18n.t('BLOCK_SERVER_ACTIONS_SUCCESSFULLY_KEY'),
        })
      ),
      put(
        getAllPartnerServersRequest({
          query,
          id: partnerUuid,
          server_type: 'preparation',
        })
      ),
    ]);
  } catch (error) {
    return yield put(blockServerPartnerActionsRejected());
  }
}

function* unblockServerPartnerActionsRequestSaga({ payload }) {
  const { partnerUuid, instanceId, values, query } = payload;
  try {
    yield api.post({
      url: endpoints.unblockServerPartnerActions(partnerUuid),
      data: { ...values, instance_ids: instanceId },
    });

    return yield all([
      put(unblockServerPartnerActionsFulfilled()),
      put(
        messagesRequest({
          type: 'success',
          message: i18n.t('UNBLOCK_SERVER_ACTIONS_SUCCESSFULLY_KEY'),
        })
      ),
      put(
        getAllPartnerServersRequest({
          query,
          id: partnerUuid,
          server_type: 'preparation',
        })
      ),
    ]);
  } catch (error) {
    return yield put(unblockServerPartnerActionsRejected());
  }
}

function* getInstancesByUsageRequestSaga({ payload }) {
  const { query, id } = payload;

  try {
    const { data } = yield api.get({
      url: endpoints.getInstancesByUsage(id, query),
    });

    return yield put(getInstancesByUsageFulfilled(data));
  } catch (error) {
    return yield put(getInstancesByUsageRejected());
  }
}

function* getLeakedCredentialsUsersRequestSaga({ payload }) {
  const { query } = payload;

  try {
    const { data } = yield api.get({
      url: endpoints.getLeakedCredentialsUsers(query),
    });

    return yield put(getLeakedCredentialsUsersFulfilled(data));
  } catch (error) {
    return yield put(getLeakedCredentialsUsersRejected());
  }
}
function* getLeakedCredentialsUsersClientsRequestSaga({ payload }) {
  const { query, id } = payload;

  try {
    const { data } = yield api.get({
      url: endpoints.getLeakedCredentialsUsersClients(id, query),
    });

    return yield put(getLeakedCredentialsUsersClientsFulfilled(data));
  } catch (error) {
    return yield put(getLeakedCredentialsUsersClientsRejected());
  }
}

function* resetPasswordLeakedCredentialsRequestSaga({ payload }) {
  const { ids, query } = payload;
  try {
    yield api.post({
      url: endpoints.resetPasswordLeakedCredentials(),
      data: { ids },
    });

    return yield all([
      put(resetPasswordLeakedCredentialsFulfilled()),
      put(
        messagesRequest({
          type: 'success',
          message: i18n.t('SOLICITED_PASSWORD_RESET_KEY'),
        })
      ),
      put(
        getLeakedCredentialsUsersRequest({
          query,
        })
      ),
    ]);
  } catch (error) {
    return yield put(resetPasswordLeakedCredentialsRejected());
  }
}

export default all([
  takeLatest(newPartnerRequest, newPartnerRequestSaga),
  takeLatest(deletePartnerRequest, deletePartnerRequestSaga),
  takeLatest(LDAPTestRequest, newLDAPTestRequestSaga),
  takeLatest(editPartnerRequest, editPartnerRequestSaga),
  takeLatest(getPartnerRequest, getPartnerRequestSaga),
  takeLatest(getAllPartnersRequest, getAllPartnersRequestSaga),
  takeLatest(getCustomStateRequest, getCustomStateRequestSaga),
  takeLatest(toggleBlockPartnerRequest, toggleBlockPartnerRequestSaga),
  takeLatest(saveCustomRequest, saveCustomRequestSaga),
  takeLatest(getAllPartnersOperatorRequest, getAllPartnersOperatorRequestSaga),
  takeLatest(publishCustomRequest, publishCustomRequestSaga),
  takeLatest(editCustomRequest, editCustomRequestSaga),
  takeLatest(removeImageCustomRequest, removeImageCustomRequestSaga),
  takeLatest(deleteCustomRequest, deleteCustomRequestSaga),
  takeLatest(getAllPartnerServersRequest, getAllPartnerServersRequestSaga),
  takeLatest(startPartnerServersRequest, startPartnerServersRequestSaga),
  takeLatest(stopPartnerServersRequest, stopPartnerServersRequestSaga),
  takeLatest(publishPartnerServersRequest, publishPartnerServersRequestSaga),
  takeLatest(clonePartnerRequest, clonePartnerRequestSaga),
  takeLatest(changeAdPasswordRequest, changeAdPasswordRequestSaga),
  takeLatest(getLDAPTypeReportRequest, getLDAPTypeReportRequestSaga),
  takeLatest(getSSOReportRequest, getSSOReportRequestSaga),
  takeLatest(getNewFrontReportRequest, getNewFrontReportRequestSaga),
  takeLatest(
    getPartnerProviderTypeReportRequest,
    getPartnerProviderTypeReportRequestSaga
  ),
  takeLatest(exportPartnerSummaryRequest, exportPartnerSummaryRequestSaga),
  takeLatest(publishImagePartnerRequest, publishImagePartnerRequestSaga),
  takeLatest(
    blockServerPartnerActionsRequest,
    blockServerPartnerActionsRequestSaga
  ),
  takeLatest(
    unblockServerPartnerActionsRequest,
    unblockServerPartnerActionsRequestSaga
  ),
  takeLatest(getInstancesByUsageRequest, getInstancesByUsageRequestSaga),
  takeLatest(
    getLeakedCredentialsUsersRequest,
    getLeakedCredentialsUsersRequestSaga
  ),
  takeLatest(
    getLeakedCredentialsUsersClientsRequest,
    getLeakedCredentialsUsersClientsRequestSaga
  ),
  takeLatest(
    resetPasswordLeakedCredentialsRequest,
    resetPasswordLeakedCredentialsRequestSaga
  ),
]);
